import React from "react";
import { ArrowDown, ArrowUp, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ICON_KEYS, SECTION_LABELS, PRODUCT_SOURCES } from "@/lib/companyProfile";
import { ImageSlotField } from "./ImageSlotField";
import { ProductPicker } from "./ProductPicker";
import { SectionShell, TextField, AreaField, UrlField, Field } from "./SectionShell";
import { ListEditor } from "./ListEditor";

/* ---- Mode & urutan section ---- */
export function ModeEditor({ cfg, set }) {
  const order = cfg.section_order || [];
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= order.length) return;
    const next = [...order];
    [next[i], next[j]] = [next[j], next[i]];
    set({ section_order: next });
  };
  return (
    <div className="space-y-5">
      <Card className="p-4 bg-blue-50 border border-blue-200 flex gap-3 text-sm text-blue-900">
        <Building2 size={18} className="shrink-0 mt-0.5" />
        <div>Saat aktif, halaman depan (<code>/</code>) menjadi Company Profile dan toko tetap di <code>/katalog</code>. Mematikan kembali mengembalikan halaman depan toko — tanpa mengubah produk, kategori, pesanan, atau tema.</div>
      </Card>
      <Card className="p-5 space-y-5" data-testid="cp-editor-mode">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="font-semibold">Aktifkan Company Profile sebagai halaman depan</div>
            <div className="text-xs text-[#8A8A8A] mt-0.5">Mode saat ini: <strong data-testid="company-profile-mode">{cfg.mode === "company_profile" ? "Company Profile + Store" : "Store Only"}</strong></div>
          </div>
          <Switch checked={!!cfg.enabled} onCheckedChange={(v) => set({ enabled: v })} data-testid="company-profile-switch" />
        </div>
        <div>
          <div className="font-semibold text-sm">Urutan section</div>
          <p className="text-xs text-[#8A8A8A] mt-0.5">Hero selalu paling atas. Section yang disembunyikan tidak tampil walau ada di urutan.</p>
          <ol className="mt-3 grid gap-1.5" data-testid="cp-section-order">
            {order.map((key, i) => (
              <li key={key} className="flex items-center justify-between rounded-lg border border-[#E5E5E5] px-3 py-2 text-sm" data-testid={`cp-order-${key}`}>
                <span className="flex items-center gap-2"><span className="text-[#8A8A8A] text-xs w-4">{i + 1}.</span>{SECTION_LABELS[key] || key}
                  {cfg[key]?.enabled ? null : <span className="text-[10px] font-bold uppercase tracking-wider text-[#8A8A8A]">tersembunyi</span>}</span>
                <span className="flex gap-1">
                  <Button type="button" size="icon" variant="ghost" className="h-7 w-7" disabled={i === 0} onClick={() => move(i, -1)} aria-label="Naik" data-testid={`cp-order-up-${key}`}><ArrowUp size={13} /></Button>
                  <Button type="button" size="icon" variant="ghost" className="h-7 w-7" disabled={i === order.length - 1} onClick={() => move(i, 1)} aria-label="Turun" data-testid={`cp-order-down-${key}`}><ArrowDown size={13} /></Button>
                </span>
              </li>
            ))}
          </ol>
        </div>
      </Card>
    </div>
  );
}

/* ---- Hero ---- */
export function HeroEditor({ data, set }) {
  const cta = (k, patch) => set({ [k]: { ...(data[k] || {}), ...patch } });
  return (
    <SectionShell title="Hero" description="Bagian pertama yang dilihat pengunjung. Jika judul kosong, nama toko dipakai." testId="cp-editor-hero">
      <TextField label="Eyebrow (label kecil)" value={data.eyebrow} maxLength={60} onChange={(v) => set({ eyebrow: v })} placeholder="Mis. Sejak 1998 · Distributor Resmi" testId="cp-hero-eyebrow" />
      <TextField label="Judul" value={data.title} maxLength={120} onChange={(v) => set({ title: v })} placeholder="Nama perusahaan / tagline utama" testId="company-hero-title-input" />
      <AreaField label="Subjudul" value={data.subtitle} maxLength={400} rows={3} onChange={(v) => set({ subtitle: v })} placeholder="Deskripsi singkat perusahaan" testId="company-hero-subtitle-input" />
      <ImageSlotField slot="hero" label="Gambar hero" value={data.image} onChange={(v) => set({ image: v })} testId="cp-hero-image" />
      <div className="grid gap-4 md:grid-cols-2">
        <TextField label="Tombol utama — label" value={data.primary_cta?.label} maxLength={60} onChange={(v) => cta("primary_cta", { label: v })} placeholder="Lihat Produk" testId="cp-hero-primary-label" />
        <UrlField label="Tombol utama — tautan" value={data.primary_cta?.url} onChange={(v) => cta("primary_cta", { url: v })} placeholder="/katalog" testId="cp-hero-primary-url" hint="Kosong = otomatis ke /katalog." />
        <TextField label="Tombol kedua — label" value={data.secondary_cta?.label} maxLength={60} onChange={(v) => cta("secondary_cta", { label: v })} placeholder="Hubungi Kami" testId="cp-hero-secondary-label" />
        <UrlField label="Tombol kedua — tautan" value={data.secondary_cta?.url} onChange={(v) => cta("secondary_cta", { url: v })} placeholder="https://wa.me/62…" testId="cp-hero-secondary-url" />
      </div>
    </SectionShell>
  );
}

/* ---- About ---- */
export function AboutEditor({ data, set }) {
  return (
    <SectionShell title="Tentang Kami" description="Cerita singkat perusahaan." enabled={data.enabled} onToggle={(v) => set({ enabled: v })} testId="cp-editor-about">
      <TextField label="Judul" value={data.title} onChange={(v) => set({ title: v })} placeholder="Tentang Perusahaan Kami" testId="cp-about-title" />
      <AreaField label="Deskripsi" value={data.description} rows={6} onChange={(v) => set({ description: v })} testId="cp-about-desc" />
      <ImageSlotField slot="about" label="Gambar" value={data.image} onChange={(v) => set({ image: v })} testId="cp-about-image" />
    </SectionShell>
  );
}

/* ---- Why Us ---- */
export function WhyUsEditor({ data, set, max = 8 }) {
  return (
    <SectionShell title="Keunggulan (Why Us)" description="Alasan pelanggan memilih Anda. Ikon hanya dari daftar." enabled={data.enabled} onToggle={(v) => set({ enabled: v })} testId="cp-editor-why-us">
      <TextField label="Judul section" value={data.title} onChange={(v) => set({ title: v })} placeholder="Mengapa Memilih Kami" testId="cp-why-title" />
      <ListEditor items={data.items || []} max={max} onChange={(items) => set({ items })} newItem={() => ({ title: "", description: "", icon: "badge-check" })} addLabel="Tambah keunggulan" testId="cp-why-items"
        renderItem={(it, update, i) => (
          <div className="grid gap-3 md:grid-cols-[1fr_1fr_160px]">
            <TextField label="Judul" value={it.title} onChange={(v) => update({ title: v })} testId={`cp-why-item-title-${i}`} />
            <TextField label="Deskripsi" value={it.description} maxLength={400} onChange={(v) => update({ description: v })} testId={`cp-why-item-desc-${i}`} />
            <Field label="Ikon">
              <Select value={it.icon || "badge-check"} onValueChange={(v) => update({ icon: v })}>
                <SelectTrigger data-testid={`cp-why-item-icon-${i}`}><SelectValue /></SelectTrigger>
                <SelectContent>{ICON_KEYS.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>
        )} />
    </SectionShell>
  );
}

/* ---- Store Preview ---- */
export function StorePreviewEditor({ data, set, maxProducts = 8 }) {
  const source = data.source || "featured";
  return (
    <SectionShell title="Pratinjau Toko" description="Menampilkan maks. 8 kategori + 8 produk dari toko Anda. Data diambil langsung dari katalog — tidak ada salinan produk." enabled={data.enabled} onToggle={(v) => set({ enabled: v })} testId="cp-editor-store-preview">
      <TextField label="Judul section" value={data.title} onChange={(v) => set({ title: v })} placeholder="Produk Kami" testId="cp-store-title" />
      <Field label="Sumber produk">
        <div className="grid gap-2 sm:grid-cols-3" role="radiogroup" data-testid="cp-store-source">
          {PRODUCT_SOURCES.map((o) => (
            <label key={o.value} className={`cursor-pointer rounded-xl border p-3 text-sm ${source === o.value ? "border-[#111827] bg-[#F9FAFB]" : "border-[#E5E5E5]"}`} data-testid={`cp-store-source-${o.value}`}>
              <span className="flex items-center gap-2 font-semibold"><input type="radio" name="cp-store-source" value={o.value} checked={source === o.value} onChange={() => set({ source: o.value })} /> {o.label}</span>
              <span className="block mt-1 text-[11px] text-[#5C5C5C] leading-relaxed">{o.hint}</span>
            </label>
          ))}
        </div>
      </Field>
      {source === "manual" ? <ProductPicker ids={data.product_ids || []} onChange={(product_ids) => set({ product_ids })} max={maxProducts} /> : (
        <p className="text-[11px] text-[#5C5C5C]" data-testid="cp-store-auto-note">Mode otomatis: bila belum ada produk yang memenuhi, storefront menampilkan produk aktif terbaru.</p>
      )}
      <TextField label="Label tombol ke katalog" value={data.cta_label} maxLength={60} onChange={(v) => set({ cta_label: v })} placeholder="Lihat Semua Produk" testId="cp-store-cta-label" hint="Tautan selalu ke /katalog." />
    </SectionShell>
  );
}

/* ---- Business / Wholesale ---- */
export function BusinessCtaEditor({ data, set }) {
  return (
    <SectionShell title="Bisnis / Grosir" description="Ajakan untuk pembelian grosir atau kerja sama. Tautan bisa WhatsApp (https://wa.me/62…) atau URL lain." enabled={data.enabled} onToggle={(v) => set({ enabled: v })} testId="cp-editor-business-cta">
      <TextField label="Judul" value={data.title} onChange={(v) => set({ title: v })} placeholder="Butuh Pasokan Rutin untuk Usaha Anda?" testId="cp-biz-title" />
      <AreaField label="Deskripsi" value={data.description} maxLength={400} rows={3} onChange={(v) => set({ description: v })} testId="cp-biz-desc" />
      <div className="grid gap-4 md:grid-cols-2">
        <TextField label="Label tombol" value={data.cta_label} maxLength={60} onChange={(v) => set({ cta_label: v })} placeholder="Hubungi Tim Kami" testId="cp-biz-cta-label" />
        <UrlField label="Tautan tombol" value={data.cta_url} onChange={(v) => set({ cta_url: v })} placeholder="https://wa.me/62…" testId="cp-biz-cta-url" hint="Section hanya tampil jika label dan tautan terisi." />
      </div>
    </SectionShell>
  );
}
